import React from 'react';
import { Box, Grid, Button, Typography, Stack } from '@mui/material';
import { AudioCard, AudioCardProps } from './Cards/AudioCard';
import { AudioReelEmptyCard } from './ReelEmptyStates';
import { ISound } from '../../types/global/helper';
import { ThemeStyle } from '../../module/ThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';

interface AudioListProps extends Pick<AudioCardProps, 'isEditMode' | 'onDelete'> {
    sounds?: ISound[];
    themeStyle: ThemeStyle;
    isEditMode?: boolean;
    onAddNewItem?: VoidFunction;
    compactSectionTitle?: boolean;
    flushLayout?: boolean;
    /** Flush empty card width with title column (voiceover; mirrors `DemoReelGrid` `alignWithTitleCard`). */
    alignWithTitleCard?: boolean;
    /** Artist: muted uppercase title + Stack layout (matches demo reel / skills left edge; no Grid item inset) */
    artistStudioCard?: boolean;
    /** Media tab: parent supplies section title + subtitle; hide this grid’s label. */
    omitSectionTitle?: boolean;
    /** Not the profile owner: neutral empty state. */
    visitorView?: boolean;
}

export const AudioReelGrid: React.FC<AudioListProps> = ({
    sounds,
    themeStyle,
    isEditMode,
    onAddNewItem,
    compactSectionTitle,
    flushLayout,
    alignWithTitleCard,
    artistStudioCard,
    omitSectionTitle,
    visitorView,
    ...props
}) => {
    if (!sounds) {
        return <div />;
    }
    const sectionTitle = sounds.length > 1 ? 'Audio Reels' : 'Audio Reel';
    const titleTypography = (
        <Typography
            sx={{
                color: artistStudioCard
                    ? 'rgba(255,255,255,0.5)'
                    : compactSectionTitle
                    ? 'rgba(255,255,255,0.45)'
                    : theme[themeStyle].highlight,
                fontFamily: FONT_ENUM.MAIN_BOLD,
                fontWeight: compactSectionTitle || artistStudioCard ? 700 : undefined,
                fontSize: artistStudioCard ? 14 : compactSectionTitle ? 10 : 16,
                letterSpacing: artistStudioCard ? '0.22em' : compactSectionTitle ? '0.14em' : undefined,
                textTransform: artistStudioCard || compactSectionTitle ? 'uppercase' : 'none',
                px: compactSectionTitle || artistStudioCard ? 0 : 2,
                mb: 0,
                width: '100%',
            }}>
            {sectionTitle}
        </Typography>
    );

    /** Same pattern as `DemoReelGrid` + `artistStudioTitle`: no Grid item padding so left edge matches demo reel & skills. */
    if (artistStudioCard) {
        return (
            <Stack spacing={3} sx={{ width: '100%', maxWidth: '100%', mt: 0, alignItems: 'stretch' }}>
                <Box sx={{ width: '100%' }}>{titleTypography}</Box>
                {sounds.map((sound, index) => (
                    <AudioCard key={sound._id || index} soundData={sound} isEditMode={isEditMode} {...props} />
                ))}
                {sounds.length === 0 && (
                    <AudioReelEmptyCard
                        themeStyle={themeStyle}
                        isEditMode={isEditMode}
                        onUpload={onAddNewItem}
                        fullWidth
                        featuredHero
                        visitorView={visitorView}
                    />
                )}
                {isEditMode && sounds.length > 0 && (
                    <Button
                        onClick={onAddNewItem}
                        sx={{
                            mt: 0,
                            width: '100%',
                            height: 44,
                            backgroundColor: theme[themeStyle].highlight,
                            color: theme[themeStyle].black,
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            '&:hover': { backgroundColor: theme[themeStyle].primary_orange },
                        }}>
                        Add Audio Reel
                    </Button>
                )}
            </Stack>
        );
    }

    const grid = (
        <Grid
            container
            spacing={flushLayout ? 1.5 : 2}
            mt={omitSectionTitle ? 0 : flushLayout ? 0 : 2}
            sx={{ width: '100%', mx: 0 }}>
            {!omitSectionTitle && titleTypography}
            {sounds.map((sound, index) => (
                <Grid
                    item
                    xs={12}
                    key={sound._id || index}
                    sx={{
                        px: flushLayout ? '0 !important' : undefined,
                        mb: { xs: 2, sm: 3 },
                    }}>
                    <AudioCard soundData={sound} isEditMode={isEditMode} {...props} />
                </Grid>
            ))}
            {sounds.length === 0 && (
                <Grid item xs={12} sx={{ px: flushLayout ? '0 !important' : undefined }}>
                    <AudioReelEmptyCard
                        themeStyle={themeStyle}
                        isEditMode={isEditMode}
                        onUpload={onAddNewItem}
                        fullWidth={alignWithTitleCard}
                        visitorView={visitorView}
                    />
                </Grid>
            )}
            {isEditMode && sounds.length > 0 && (
                <Button
                    onClick={onAddNewItem}
                    sx={{
                        mt: 3,
                        width: '100%',
                        height: 44,
                        backgroundColor: theme[themeStyle].highlight,
                        color: theme[themeStyle].black,
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        '&:hover': { backgroundColor: theme[themeStyle].primary_orange },
                    }}>
                    Add Audio Reel
                </Button>
            )}
        </Grid>
    );

    return grid;
};
